import { appointmentFields, type NetworkSchematic, type SchematicPoint } from '@ghp/domain'

import { outfallTips, symbolExtent, tagBox } from './schematic-document'
import type { SchematicViewport } from './schematic-viewport'

/**
 * Le **cadrage** de l'image rendue du schéma (M11) : quelle portion du plan l'archive garde, et
 * en combien de pixels.
 *
 * ⚠️ Isolé du composant pour la raison qui a fait naître `signature-bitmap.ts` : jsdom n'a ni
 * décodeur d'image ni `canvas.toBlob`, donc la rastérisation ne s'exerce qu'au navigateur. Ce
 * qui se paie cher quand on le rate — la marge, le plancher, le plafond, la proportion — tient
 * ici, et s'éprouve.
 *
 * ⚠️ **Le cadre suit le dessin, pas la vue de l'agent.** Exporter ce qu'il a sous les yeux
 * livrerait au bureau une image dont la moitié du branchement est hors champ, selon l'endroit
 * où l'agent avait zoomé en refermant la fiche.
 */

/** La marge autour du dessin. Sans elle, le trait s'arrête au bord et paraît coupé. */
export const SCHEMATIC_EXPORT_MARGIN = 40

/**
 * Le côté minimal du cadre, en unités de document. Un élément posé seul a une boîte de zéro
 * par zéro : sans plancher, l'archive garderait une vignette de la taille d'une marge.
 */
export const SCHEMATIC_EXPORT_MIN_SIDE = 320

/**
 * Les pixels par unité de document. Le plan à 100 % fait 1600 unités de large, l'écran d'une
 * tablette aussi : à un pixel par unité, l'image tombait floue dès que le bureau zoomait (recette
 * du 20/10, BT02). Deux, c'est la densité de l'écran de la tablette.
 */
export const SCHEMATIC_EXPORT_DENSITY = 2

/**
 * Le côté le plus long de l'image, en pixels.
 *
 * ⚠️ Il borne la **mémoire du canvas** et le **poids du JPEG**, qui part dans la même file
 * d'envoi que les photos. 4096 × 4096 est la plus grande surface qu'un canvas Safari accepte
 * (16,7 Mpx), et le bureau rend lui aussi l'image : au-delà, le rendu échoue et l'image manque.
 * Mesuré le 20/10/2026 : un plan de 5000 unités sort en 4096 px pour 140 Ko.
 */
export const SCHEMATIC_EXPORT_MAX_SIDE = 4096

export interface SchematicExportFrame {
  /** La portion du plan à rendre, en unités de document. */
  view: SchematicViewport
  /** La taille de l'image, en pixels. */
  width: number
  height: number
}

/**
 * Le cadre de l'image d'un dessin, ou `null` quand il n'y a rien à archiver.
 *
 * `null` plutôt qu'une image blanche : une pièce jointe vide dirait au bureau qu'un schéma
 * existe, et il chercherait ce qu'il n'y a pas.
 */
export function schematicExportFrame(schematic: NetworkSchematic): SchematicExportFrame | null {
  const boxes = schematic.nodes.flatMap(boxesOf)
  const first = boxes[0]
  if (first === undefined) return null

  // ⚠️ Une boucle, pas `Math.min(...boxes)` : un plan peut porter des centaines de milliers de
  // points, et l'étalement d'autant d'arguments dépasse la pile.
  let minX = first.x
  let minY = first.y
  let maxX = first.x + first.width
  let maxY = first.y + first.height
  for (const box of boxes) {
    minX = Math.min(minX, box.x)
    minY = Math.min(minY, box.y)
    maxX = Math.max(maxX, box.x + box.width)
    maxY = Math.max(maxY, box.y + box.height)
  }

  const view = expanded({
    x: minX - SCHEMATIC_EXPORT_MARGIN,
    y: minY - SCHEMATIC_EXPORT_MARGIN,
    width: maxX - minX + SCHEMATIC_EXPORT_MARGIN * 2,
    height: maxY - minY + SCHEMATIC_EXPORT_MARGIN * 2,
  })

  // ⚠️ Une seule échelle pour les deux côtés : deux facteurs plafonneraient le poids en
  // écrasant le dessin, et une proportion perdue ne se rattrape pas après coup.
  const scale = Math.min(
    SCHEMATIC_EXPORT_DENSITY,
    SCHEMATIC_EXPORT_MAX_SIDE / Math.max(view.width, view.height),
  )

  return { view, width: Math.round(view.width * scale), height: Math.round(view.height * scale) }
}

/**
 * Ce qu'une forme occupe à l'écran : ses points, plus le rayon d'un symbole ou le texte d'une
 * étiquette. ⚠️ Les points seuls coupaient l'image au milieu d'un tampon agrandi ou d'une
 * étiquette posée en bordure — la marge ne couvre que le trait et sa pointe.
 */
function boxesOf(node: NetworkSchematic['nodes'][number]): SchematicViewport[] {
  const tag = tagBox(node)
  if (tag !== null) return [tag]
  const reach = node.points.length === 1 ? symbolExtent(node) : 0
  return reachedPoints(node).map((point) => ({
    x: point.x - reach,
    y: point.y - reach,
    width: reach * 2,
    height: reach * 2,
  }))
}

/**
 * Les points que le dessin atteint. Ceux du document, sauf pour la gouttière sur sol : ses deux
 * flèches s'écartent du geste, et le cadre tiré sur lui seul en coupait les pointes.
 */
function reachedPoints(node: NetworkSchematic['nodes'][number]): SchematicPoint[] {
  const [first, second] = node.points
  if (node.kind !== 'downpipeOutfall' || first === undefined || second === undefined) {
    return node.points
  }
  return [first, ...outfallTips(first, second)]
}

/** Le plancher, appliqué **autour du centre** : sinon la forme unique sort du cadre. */
function expanded(view: SchematicViewport): SchematicViewport {
  const width = Math.max(view.width, SCHEMATIC_EXPORT_MIN_SIDE)
  const height = Math.max(view.height, SCHEMATIC_EXPORT_MIN_SIDE)

  return {
    x: view.x + view.width / 2 - width / 2,
    y: view.y + view.height / 2 - height / 2,
    width,
    height,
  }
}

/**
 * L'**empreinte du dessin** qu'une image d'archive montre : deux dessins identiques la
 * partagent, un trait déplacé d'un dixième d'unité la change.
 *
 * ⚠️ Elle existe pour une seule raison, et c'est un défaut mesuré : le bureau ne voit **que**
 * l'image. Un agent qui appuie sur « JPEG » puis se souvient de la fosse et la pose laisse
 * partir une archive amputée, sur un contrôle que la transmission rend immuable — et aucun
 * écran ne le disait, l'horodatage de l'export s'affichant quoi qu'il arrive. Rangée à côté de
 * l'image ({@link LocalMedium.sourceFingerprint}), elle survit au rechargement de la page, là
 * où une mémoire tenue dans l'éditeur repartirait à zéro.
 *
 * ⚠️ **Une empreinte, pas une signature** : une collision fait manquer un avertissement, elle
 * ne corrompt rien. Le sens de l'échec est celui qu'on veut — au pire l'agent n'est pas averti
 * d'une retouche, jamais une image valide déclarée périmée.
 */
export function schematicFingerprint(schematic: NetworkSchematic): string {
  const text = JSON.stringify(schematic)
  // FNV-1a 32 bits : quelques lignes, aucune dépendance, et le Service Worker d'une PWA de
  // terrain n'a pas à précacher une bibliothèque de hachage pour comparer deux dessins.
  let hash = 0x811c9dc5

  for (let index = 0; index < text.length; index++) {
    hash ^= text.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }

  return `${(hash >>> 0).toString(36)}-${text.length.toString(36)}`
}

/** L'image d'archive telle qu'elle se lit ici : son type, et le dessin qu'elle montre. */
export interface ArchivedSchematicImage {
  type: string
  sourceFingerprint?: string | null
}

/**
 * L'image d'archive jointe **ne montre plus** le dessin en cours.
 *
 * ⚠️ Une seule règle pour deux écrans : l'éditeur remplace sa phrase par l'avertissement, et
 * l'onglet « Transmettre » l'ajoute à la liste que l'agent relit avant un geste irréversible.
 * Calculée à deux endroits, elle finirait par dire deux choses sur le même contrôle.
 *
 * ⚠️ **Une image dont l'empreinte manque est déclarée périmée**, jamais à jour : le sens de
 * l'erreur est celui qu'on veut (M21). Se tromper d'un côté coûte un export de plus, de l'autre
 * une archive amputée sur un contrôle que la transmission a rendu immuable.
 *
 * Un dessin vide ne déclenche rien : effacer le plan **emporte** son image (voir le conteneur
 * de l'éditeur), et un dessin jamais exporté relève de l'autre avertissement.
 */
export function hasOutdatedSchematicImage(
  schematic: NetworkSchematic | undefined,
  media: ArchivedSchematicImage[],
): boolean {
  if (schematic === undefined || schematic.nodes.length === 0) return false

  const image = media.find((medium) => medium.type === 'schematic')

  if (image === undefined) return false

  return image.sourceFingerprint !== schematicFingerprint(schematic)
}

/**
 * Le nom du fichier qu'on retrouve dans ses téléchargements, à la tablette comme au bureau.
 *
 * ⚠️ **Le numéro de dossier d'abord**, comme le titre de la fiche (`inspectionTitle`) : c'est le
 * numéro que le client emploie et dont il parle au téléphone. La référence GHP ne prend sa place
 * que s'il manque — les deux sont nullables (M1 : l'unicité est portée par l'`id`).
 *
 * ⚠️ **La référence vient du client, pas de nous.** Elle porte des barres obliques, des accents
 * et des espaces ; une barre oblique y désignerait un dossier, et le téléchargement échouerait
 * ou atterrirait ailleurs. Tout ce qui n'est pas une lettre, un chiffre, un point, un tiret ou
 * un souligné devient un tiret — et un nom qui se réduirait à de la ponctuation retombe sur le
 * nom nu, un fichier caché sur la tablette étant un fichier perdu.
 *
 * ⚠️ **Un dépôt daté (`dated`, instant ISO) prend son jour de Paris dans le nom**, sans quoi deux
 * versions s'écraseraient ; le jour UTC d'un dépôt de 00:30 serait la veille.
 */
export function schematicFileName(
  inquiry: { caseReference: string | null; reference: string | null } | null,
  dated: string | null = null,
): string {
  const chosen = inquiry?.caseReference ?? inquiry?.reference ?? null
  const safe = (chosen ?? '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  const day = dated === null ? undefined : appointmentFields(dated)?.day

  return `${safe === '' ? 'schema' : `${safe}-schema`}${day === undefined ? '' : `-${day}`}.jpg`
}
